import React from 'react';
import { motion } from 'framer-motion';
import { GENERAL_IMAGES, getRandomImage, getImage } from '../../constants/images';

// Reusable Image Component with various configurations
const ImageComponent = ({
  src,
  alt = "Image",
  className = "",
  category = null,
  imageKey = null,
  random = false,
  fallback = null,
  lazy = true,
  motionProps = {},
  overlay = false,
  overlayColor = "rgba(0, 0, 0, 0.3)",
  ...props
}) => {
  // Determine the image source
  let imageSrc = src;
  
  if (!imageSrc) {
    if (category && imageKey) {
      imageSrc = getImage(category, imageKey);
    } else if (category && random) {
      imageSrc = getRandomImage(category);
    } else if (random) {
      imageSrc = getRandomImage();
    }
  }
  
  // Use fallback if no image found
  if (!imageSrc && fallback) {
    imageSrc = fallback;
  }

  const ImageElement = motion.img;

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <ImageElement
        src={imageSrc}
        alt={alt}
        loading={lazy ? "lazy" : "eager"}
        className="w-full h-full object-cover"
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        whileHover={{ scale: 1.05 }}
        {...motionProps}
        {...props}
      />
      {overlay && (
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: overlayColor }}
        />
      )}
    </div>
  );
};

// Background Image Component
export const BackgroundImage = ({
  src,
  category = null,
  imageKey = null,
  random = false,
  className = "",
  overlay = true,
  overlayColor = "rgba(0, 0, 0, 0.4)",
  children,
  ...props
}) => {
  let imageSrc = src;
  
  if (!imageSrc) {
    if (category && imageKey) {
      imageSrc = getImage(category, imageKey);
    } else if (category && random) {
      imageSrc = getRandomImage(category);
    } else if (random) {
      imageSrc = getRandomImage();
    }
  }

  return (
    <div 
      className={`relative bg-cover bg-center bg-no-repeat ${className}`}
      style={{ backgroundImage: `url(${imageSrc})` }}
      {...props}
    >
      {overlay && (
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: overlayColor }}
        />
      )}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

// Card Image Component
export const CardImage = ({
  src,
  category = null,
  imageKey = null,
  random = false,
  alt = "Card Image",
  className = "",
  aspectRatio = "aspect-video",
  ...props
}) => {
  return (
    <ImageComponent
      src={src}
      category={category}
      imageKey={imageKey}
      random={random}
      alt={alt}
      className={`${aspectRatio} ${className}`}
      motionProps={{
        whileHover: { scale: 1.02 },
        transition: { duration: 0.3 }
      }}
      {...props}
    />
  );
};

// Hero Image Component
export const HeroImage = ({
  src,
  category = null,
  imageKey = null,
  alt = "Hero Image",
  className = "",
  overlay = true,
  overlayColor = "rgba(0, 0, 0, 0.5)",
  children,
  ...props
}) => {
  return (
    <BackgroundImage
      src={src}
      category={category}
      imageKey={imageKey}
      className={`min-h-screen ${className}`}
      overlay={overlay}
      overlayColor={overlayColor}
      {...props}
    >
      {children}
    </BackgroundImage>
  );
};

// News Image Component
export const NewsImage = ({
  src,
  category = "news",
  imageKey = null,
  random = true,
  alt = "News Image",
  className = "",
  ...props
}) => {
  return (
    <CardImage
      src={src}
      category={category}
      imageKey={imageKey}
      random={random}
      alt={alt}
      className={className}
      aspectRatio="aspect-[16/10]"
      {...props}
    />
  );
};

// Project Image Component
export const ProjectImage = ({
  src,
  category = "projects",
  imageKey = null,
  random = true,
  alt = "Project Image",
  className = "",
  ...props
}) => {
  return (
    <CardImage
      src={src}
      category={category}
      imageKey={imageKey}
      random={random}
      alt={alt}
      className={className}
      aspectRatio="aspect-[4/3]"
      {...props}
    />
  );
};

export default ImageComponent;
